import { Stack, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Box, FlatList } from 'native-base';
import React, { useEffect } from 'react';

import {
  TableHeadContainer,
  TableHeadItem,
  TableHeadItemOrderBy,
} from '../../../../../../components/TableHead';
import { useScrollCloseToBottomContext } from '../../../../../../contexts/ScrollCloseToBottom';
import { FixedFeeTerm } from '../../../../../../store/fixedFee/types';

import { FixedFeeTermsListBottom } from './components/FixedFeeTermsListBottom';
import { FixedFeeTermsListEmpty } from './components/FixedFeeTermsListEmpty';
import { FixedFeeTermsRow, FixedFeeTermsRowLoading } from './components/FixedFeeTermsRow';
import { fixedFeeTermsTableHead } from './constants';

export interface FixedFeeTermsListProps {
  items: FixedFeeTerm[] | undefined;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasFilter?: boolean;
  orderBy?: TableHeadItemOrderBy;
  onOrderBy?: (value: string) => void;
  onEndReached: () => void;
  currentPage: number;
  disabled: boolean;
}

export const FixedFeeTermsList: React.FC<FixedFeeTermsListProps> = ({
  items,
  isLoading,
  isLoadingMore,
  hasFilter = false,
  orderBy,
  onOrderBy,
  currentPage,
  onEndReached,
  disabled,
}) => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const isDesktop = useBreakpointValue({ base: false, sm: false, lg: true });

  const { isCloseToBottom } = useScrollCloseToBottomContext();

  const loadingInitialProducts = isLoading && currentPage === 1;

  const renderContent = () => {
    if (disabled) {
      return <FixedFeeTermsListEmpty hasFilter={hasFilter} disabled={disabled} />;
    }
    if (loadingInitialProducts) {
      return (
        <VStack space="stack.nano">
          {Array.from({ length: 5 }).map((_, index) => (
            <FixedFeeTermsRowLoading key={index} />
          ))}
        </VStack>
      );
    }

    return (
      <FlatList
        data={items}
        keyExtractor={(item) => item.id}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          gap: Number(Spacing['stack.nano']) || 8,
          paddingBottom: Number(Typography['fontsize.lg']) || 16,
          maxHeight: isDesktop ? 600 : undefined,
        }}
        onEndReached={!isMobile ? onEndReached : undefined}
        onEndReachedThreshold={currentPage === 1 ? 0.15 : 0.5}
        ListEmptyComponent={
          <Box py={Spacing['inline.xs']}>
            <FixedFeeTermsListEmpty hasFilter={hasFilter} />
          </Box>
        }
        ListFooterComponent={
          <FixedFeeTermsListBottom dataLength={items?.length || 0} isLoadingMore={isLoadingMore} />
        }
        renderItem={({ item }) => <FixedFeeTermsRow item={item} />}
      />
    );
  };

  useEffect(() => {
    if (isCloseToBottom && !isLoading && isMobile) {
      onEndReached();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCloseToBottom, isLoading, isMobile]);

  return (
    <VStack space="stack.nano" flex={1}>
      {isDesktop && (
        <Stack px="inline.xxs">
          <TableHeadContainer>
            {fixedFeeTermsTableHead.map((item, index) => (
              <TableHeadItem
                key={`${item.title}-${index}`}
                title={item.title}
                flex={item.flex}
                value={item.orderValue}
                orderBy={orderBy}
                onPress={() => item.orderValue && onOrderBy?.(item.orderValue as string)}
              />
            ))}
          </TableHeadContainer>
        </Stack>
      )}

      {renderContent()}
    </VStack>
  );
};
